// Comprehensive error handling and logging system
class ErrorHandler {
  constructor() {
    this.errorLog = [];
    this.maxLogSize = 100;
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  // Log error with context
  logError(error, context = {}) {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      message: error.message || 'Unknown error',
      stack: error.stack,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId()
    };

    // Add to local log
    this.errorLog.unshift(errorEntry);
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog.pop();
    }

    // Console logging in development
    if (this.isDevelopment) {
      console.error('Error logged:', errorEntry);
    }

    // Send to external logging service in production
    if (!this.isDevelopment) {
      this.sendToLoggingService(errorEntry);
    }

    return errorEntry;
  }

  // Get user-friendly error message
  getUserFriendlyMessage(error) {
    const errorMessages = {
      'auth/user-not-found': 'User account not found. Please check your email.',
      'auth/wrong-password': 'Incorrect password. Please try again.',
      'auth/email-already-in-use': 'This email is already registered.',
      'auth/weak-password': 'Password is too weak. Please choose a stronger password.',
      'auth/invalid-email': 'Invalid email address format.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      'auth/network-request-failed': 'Network error. Please check your connection.',
      'permission-denied': 'You do not have permission to perform this action.',
      'unavailable': 'Service temporarily unavailable. Please try again later.',
      'deadline-exceeded': 'Request timed out. Please try again.',
      'failed-precondition': 'Operation cannot be completed at this time.',
      'out-of-range': 'The requested data is out of range.',
      'unimplemented': 'This feature is not yet implemented.',
      'internal': 'An internal error occurred. Please try again.',
      'unauthenticated': 'Please log in to continue.',
      'not-found': 'The requested resource was not found.',
      'already-exists': 'This resource already exists.',
      'resource-exhausted': 'Service limit reached. Please try again later.',
      'cancelled': 'Operation was cancelled.',
      'data-loss': 'Data loss occurred. Please try again.',
      'unknown': 'An unknown error occurred. Please try again.'
    };

    // Check for Firebase error codes
    if (error.code && errorMessages[error.code]) {
      return errorMessages[error.code];
    }

    // Check for custom error messages
    if (error.message && errorMessages[error.message]) {
      return errorMessages[error.message];
    }

    // Check for network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return 'Network error. Please check your internet connection.';
    }

    // Check for validation errors
    if (error.name === 'ValidationError') {
      return error.message;
    }

    // Default message
    return this.isDevelopment ? error.message : 'Something went wrong. Please try again.';
  }

  // Handle API errors
  handleApiError(error, operation = 'API call') {
    const friendlyMessage = this.getUserFriendlyMessage(error);
    
    this.logError(error, { operation });
    
    return {
      success: false,
      message: friendlyMessage,
      error: this.isDevelopment ? error : null
    };
  }

  // Handle Firebase errors
  handleFirebaseError(error, operation = 'Firebase operation') {
    const friendlyMessage = this.getUserFriendlyMessage(error);
    
    this.logError(error, { operation, type: 'firebase' });
    
    return {
      success: false,
      message: friendlyMessage,
      error: this.isDevelopment ? error : null
    };
  }

  // Handle form validation errors
  handleValidationError(errors, formName = 'form') {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      type: 'validation',
      form: formName,
      errors,
      url: window.location.href
    };

    this.errorLog.unshift(errorEntry);
    
    return {
      success: false,
      message: 'Please correct the errors below',
      errors
    };
  }

  // Handle file upload errors
  handleFileError(error, fileName = 'unknown') {
    const errorMessages = {
      'file-too-large': 'File is too large. Please choose a smaller file.',
      'file-type-not-allowed': 'File type not allowed. Please choose a different file.',
      'upload-failed': 'File upload failed. Please try again.',
      'network-error': 'Network error during upload. Please check your connection.'
    };

    const friendlyMessage = errorMessages[error.code] || 'File upload failed. Please try again.';
    
    this.logError(error, { fileName, type: 'file-upload' });
    
    return {
      success: false,
      message: friendlyMessage,
      error: this.isDevelopment ? error : null
    };
  }

  // Retry mechanism for failed operations
  async retryOperation(operation, maxRetries = 3, delay = 1000) {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        
        if (attempt === maxRetries) {
          this.logError(error, { 
            operation: 'retry-operation', 
            attempts: maxRetries,
            finalAttempt: true 
          });
          throw error;
        }
        
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt - 1)));
      }
    }
    
    throw lastError;
  }

  // Get current user ID for error context
  getCurrentUserId() {
    try {
      const userSession = localStorage.getItem('userSession');
      if (userSession) {
        const session = JSON.parse(userSession);
        return session.user?.uid || 'unknown';
      }
      
      const tutorSession = localStorage.getItem('tutorSession');
      if (tutorSession) {
        const session = JSON.parse(tutorSession);
        return session.tutor?.uid || 'unknown';
      }
      
      const adminSession = localStorage.getItem('adminSession');
      if (adminSession) {
        const session = JSON.parse(adminSession);
        return session.admin?.uid || 'admin';
      }
      
      return 'anonymous';
    } catch (error) {
      return 'unknown';
    }
  }

  // Send error to external logging service
  async sendToLoggingService(errorEntry) {
    try {
      // In production, you would send to services like:
      // - Sentry
      // - LogRocket
      // - Bugsnag
      // - Custom logging endpoint
      
      // For now, we'll just store in localStorage as backup
      const existingLogs = JSON.parse(localStorage.getItem('errorLogs') || '[]');
      existingLogs.unshift(errorEntry);
      
      // Keep only last 50 errors in localStorage
      if (existingLogs.length > 50) {
        existingLogs.splice(50);
      }
      
      localStorage.setItem('errorLogs', JSON.stringify(existingLogs));
    } catch (error) {
      console.error('Failed to send error to logging service:', error);
    }
  }

  // Get error statistics
  getErrorStats() {
    const now = new Date();
    const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    const recentErrors = this.errorLog.filter(error => 
      new Date(error.timestamp) > last24Hours
    );
    
    const errorTypes = recentErrors.reduce((acc, error) => {
      const type = error.context?.type || 'unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    
    return {
      totalErrors: this.errorLog.length,
      recentErrors: recentErrors.length,
      errorTypes,
      lastError: this.errorLog[0] || null
    };
  }

  // Clear error log
  clearErrorLog() {
    this.errorLog = [];
    localStorage.removeItem('errorLogs');
  }

  // Global error boundary handler
  handleGlobalError(error, errorInfo) {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      type: 'global-error',
      url: window.location.href,
      userId: this.getCurrentUserId()
    };

    this.logError(error, { type: 'global-error', errorInfo });
    
    return {
      hasError: true,
      error: this.getUserFriendlyMessage(error),
      errorId: errorEntry.timestamp
    };
  }
}

// Create global instance
const errorHandler = new ErrorHandler();

// Export functions
export const logError = (error, context) => errorHandler.logError(error, context);
export const getUserFriendlyMessage = (error) => errorHandler.getUserFriendlyMessage(error);
export const handleApiError = (error, operation) => errorHandler.handleApiError(error, operation);
export const handleFirebaseError = (error, operation) => errorHandler.handleFirebaseError(error, operation);
export const handleValidationError = (errors, formName) => errorHandler.handleValidationError(errors, formName);
export const handleFileError = (error, fileName) => errorHandler.handleFileError(error, fileName);
export const retryOperation = (operation, maxRetries, delay) => errorHandler.retryOperation(operation, maxRetries, delay);
export const getErrorStats = () => errorHandler.getErrorStats();
export const clearErrorLog = () => errorHandler.clearErrorLog();
export const handleGlobalError = (error, errorInfo) => errorHandler.handleGlobalError(error, errorInfo);

export default errorHandler;


